import React, { useState, useEffect } from 'react';
import Select from "react-select";
import { Button } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { PuffLoader } from 'react-spinners';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MDBDataTable } from 'mdbreact';
import { baseURL } from '../base_url';
import "./VinMovement.css";
import { FaFileAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const VinMovement = () => {
    const navigate = useNavigate();
    const loggedInUser = sessionStorage.getItem("tm_token");
    const foundUser = JSON.parse(loggedInUser);

    const [loading, setLoading] = useState(false);
    const [locationItems, setLocationItems] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [tableData, setTableData] = useState([]);

    /* ---------------- LOAD LOCATIONS ---------------- */
    useEffect(() => {
        axios
            .get(`${baseURL}/get_location_setup?status=active&admin_id=${foundUser.tm_id}`)
            .then((res) => {
                setLocationItems(
                    res.data.data.map(loc => ({
                        label: loc.location_name,
                        value: loc._id
                    }))
                );
            });
    }, []);

    /* ---------------- FETCH DATA ---------------- */
    const fetchVinMovement = async () => {
        if (!selectedLocation) {
            Swal.fire("Please select a location");
            return;
        }
        try {
            setLoading(true);

            const res = await axios.get(
                `${baseURL}/get_vins_last_movement`,
                { params: { loc_id: selectedLocation.value } }
            );

            if (res.data.status === 1) {
                setTableData(res.data.data);
            } else {
                Swal.fire("No data found");
            }

        } catch (err) {
            Swal.fire("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- EXPORT EXCEL ---------------- */
    const exportToExcel = () => {
        if (tableData.length === 0) {
            Swal.fire("No data to export");
            return;
        }
        const formattedData = tableData.map(row => ({
            "VIN Number": row.vin,
            "Status": row.status,
            "Last Geofence DateTime": row.last_geofence_datetime,
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const headerStyle = {
            font: { bold: true },
            alignment: { horizontal: "center", vertical: "center" }
        };
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        // Apply style to header row
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
            if (worksheet[cellAddress]) {
                worksheet[cellAddress].s = headerStyle;
            }
        }
        /* ---------------- COLUMN ALIGNMENT ---------------- */
        for (let R = 1; R <= range.e.r; ++R) {
            // VIN (left)
            worksheet[XLSX.utils.encode_cell({ r: R, c: 0 })].s = {
                alignment: { horizontal: "left" }
            };

            // Status (center)
            worksheet[XLSX.utils.encode_cell({ r: R, c: 1 })].s = {
                alignment: { horizontal: "center" }
            };

            // DateTime (center)
            worksheet[XLSX.utils.encode_cell({ r: R, c: 2 })].s = {
                alignment: { horizontal: "center" }
            };
        }

        /* ---------------- COLUMN WIDTH ---------------- */
        worksheet["!cols"] = [
            { wch: 20 }, // VIN
            { wch: 18 }, // Status
            { wch: 28 }  // DateTime
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VIN Movement");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob(
            [excelBuffer],
            { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
        );

        saveAs(blob, `${selectedLocation.label}_VIN_Movement.xlsx`);
    };

    const tableConfig = {
        columns: [
            {
                label: "VIN Number",
                field: "vin",
                sort: "asc"
            },
            {
                label: "Status",
                field: "status",
                sort: "asc"
            },
            {
                label: "Last Geofence DateTime",
                field: "last_geofence_datetime",
                sort: "asc"
            },
            {
                label: "Action",
                field: "action",
                sort: "disabled"
            }
        ],
        rows: tableData.map(row => ({
            ...row,
            action: (
                <FaFileAlt
                    title="View Trip"
                    style={{ cursor: "pointer", color: "#0d6efd" }}
                    onClick={() => handleRefreshClick(row.vin)}
                />
            )
        }))
    };

    const handleRefreshClick = (vin) => {
        // navigate(`/r_trips?vin=${vin}`);
        window.open(`/r_trips?vin=${vin}`, "_blank")
    };

    return (
        <div className="body_class">

            {loading && (
                <div className="loader-overlay">
                    <PuffLoader color="#00308F" size={120} />
                </div>
            )}

            <h4>VIN Movement</h4>

            {/* -------- FILTER -------- */}
            <div className="row p-3 bg-white rounded shadow-sm">
                <div className="col-md-4">
                    <div className='row'>
                        <label className='col-3'>Location :</label>
                        <Select className='col-7'
                            options={locationItems}
                            value={selectedLocation}
                            onChange={setSelectedLocation}
                        />
                    </div>
                    
                </div>

                <div className="col-md-8 d-flex align-items-end gap-2">
                    <Button
                        variant="contained"
                        onClick={fetchVinMovement}
                        disabled={!selectedLocation}
                    >
                        Fetch Data
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={exportToExcel}
                        disabled={tableData.length === 0}
                    >
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* -------- TABLE -------- */}
            {tableData.length > 0 && (
                <MDBDataTable
                    striped
                    bordered
                    hover
                    responsive
                    large={false}
                    data={tableConfig}
                    entries={10}
                    entriesOptions={[10, 25, 50, 100]}
                    searchLabel="Search VIN"
                    noBottomColumns={true}
                    displayEntries={false}
                />
            )}

        </div>
    );
};

export default VinMovement;